import GradeLessonPage from '../components/GradeLessonPage';
import { algebra2Topic1, algebra2Topic2, algebra2Topic3 } from './data-part1';
import { algebra2Topic4, algebra2Topic5, algebra2Topic6 } from './data-part2';
import { algebra2Topic7, algebra2Topic8, algebra2StateTestPrep } from './data-part3';

const buildSkill = (topic: {
  id: string;
  title: string;
  standards: { commonCore: string[]; nctm: string; states: Record<string, string[]> };
  explanation: string;
  practiceQuestions: { question: string; solution: string }[];
}) => ({
  id: `${topic.id}-skill`,
  title: topic.title,
  standards: [
    ...topic.standards.commonCore.map((s) => `Common Core ${s}`),
    `NCTM ${topic.standards.nctm}`,
    ...Object.entries(topic.standards.states).flatMap(([state, codes]) =>
      codes.map((c) => `${state} ${c}`)
    ),
  ],
  explanation: topic.explanation,
  questions: topic.practiceQuestions.map((q) => ({
    q: q.question,
    steps: q.solution.split('\n').filter(Boolean),
    answer: q.solution.split('\n').filter(Boolean).slice(-1)[0].replace('**Answer: ', '').replace('**', ''),
  })),
});

const algebra2GradeData = {
  grade: 'Algebra 2 & Trigonometry',
  title: 'Grade 11 — Algebra 2 & Trigonometry',
  description:
    "Master the complete Algebra 2 & Trigonometry curriculum with Mr. Augustine's clear, step-by-step explanations",
  topics: [
    {
      id: 'complex-numbers',
      title: 'Complex Numbers',
      icon: 'ri-function-line',
      color: 'violet',
      skills: [buildSkill(algebra2Topic1)],
    },
    {
      id: 'polynomial-functions',
      title: 'Polynomial Functions',
      icon: 'ri-bar-chart-grouped-line',
      color: 'teal',
      skills: [buildSkill(algebra2Topic2)],
    },
    {
      id: 'rational-functions',
      title: 'Rational Functions & Expressions',
      icon: 'ri-divide-line',
      color: 'orange',
      skills: [buildSkill(algebra2Topic3)],
    },
    {
      id: 'exponential-logarithmic',
      title: 'Exponential & Logarithmic Functions',
      icon: 'ri-line-chart-line',
      color: 'amber',
      skills: [buildSkill(algebra2Topic4)],
    },
    {
      id: 'trigonometric-functions',
      title: 'Trigonometric Functions',
      icon: 'ri-compass-3-line',
      color: 'green',
      skills: [buildSkill(algebra2Topic5)],
    },
    {
      id: 'trig-identities-equations',
      title: 'Trigonometric Identities & Equations',
      icon: 'ri-equalizer-line',
      color: 'rose',
      skills: [buildSkill(algebra2Topic6)],
    },
    {
      id: 'sequences-series',
      title: 'Sequences & Series',
      icon: 'ri-list-ordered',
      color: 'indigo',
      skills: [buildSkill(algebra2Topic7)],
    },
    {
      id: 'conic-sections',
      title: 'Conic Sections',
      icon: 'ri-shape-line',
      color: 'cyan',
      skills: [buildSkill(algebra2Topic8)],
    },
    {
      id: 'state-test-prep-alg2',
      title: 'Preparing for State Tests',
      icon: 'ri-award-line',
      color: 'rose',
      skills: [
        {
          id: 'alg2-state-test',
          title: 'Algebra 2 & Trig State Test Practice — 60 Questions',
          standards: [
            'Common Core HSN-CN, HSA-APR, HSF-TF, HSG-GPE',
            'NY Algebra 2 & Trigonometry Regents',
            'OH State Algebra 2 Assessment',
            'PA Keystone Algebra 2',
            'VA SOL Algebra 2 & Trig',
            'TX STAAR Algebra 2',
            'NCTM Standards High School',
          ],
          explanation: `Mr. Augustine's State Test Strategy Guide for Algebra 2 & Trig:

These tests cover complex numbers, polynomials, rational functions, exponential/logarithmic functions, trigonometry, sequences/series, and conic sections.

TOP TIPS:
(1) Complex numbers — remember i²=−1 and the cycle i, −1, −i, 1.
(2) Polynomials — Remainder Theorem: remainder = P(a) when dividing by (x−a).
(3) Logarithms — log and exponential are INVERSES. logₐ(x)=y means aʸ=x.
(4) Trig — memorize the unit circle values for 0°, 30°, 45°, 60°, 90°.
(5) ASTC — All Students Take Calculus tells you which functions are positive in each quadrant.
(6) Sequences — arithmetic uses addition (d), geometric uses multiplication (r).
(7) Conics — identify by the signs and equality of x² and y² coefficients.

Mr. Augustine says: 'Show ALL your work on state tests — partial credit can save your grade! And always check your answers by substituting back in!'`,
          questions: algebra2StateTestPrep.questions.map((q) => ({
            q: q.question,
            steps: [q.solution],
            answer: q.answer,
          })),
        },
      ],
    },
  ],
};

export default function Algebra2Page() {
  return (
    <GradeLessonPage
      gradeData={algebra2GradeData}
      prevGradeLink="/geometry"
      prevGradeLabel="Geometry"
      nextGradeLink="/precalc"
      nextGradeLabel="Pre-Calculus & Calculus"
    />
  );
}